import { action, makeObservable } from 'mobx'

import {
  BicCompletionMode,
  IPermitTypeField,
  IStepSubmission,
  OperationSubjectType,
  OperationType,
  PermitFieldType,
} from '~/client/graph'
import { SquareBracket } from '~/client/src/shared/enums/Brackets'
import { TagType } from '~/client/src/shared/enums/TagType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import OperationRule, {
  COMPANY_ASSIGNEE_TAG_TYPE,
} from '~/client/src/shared/models/OperationRule'
import User from '~/client/src/shared/models/User'
import Workflow from '~/client/src/shared/models/Workflow'
import WorkflowType, {
  BicAssignees,
} from '~/client/src/shared/models/WorkflowType'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import OperationRulesStore from '~/client/src/shared/stores/domain/OperationRules.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import { TAG_REG_EXP } from '~/client/src/shared/utils/regExpPatterns'

const USER_TAG_PREFIX = `${SquareBracket.LEFT}${TagType.User}]`
export const PERMIT_TYPE_TAG = 'permitType'

/** One `[permitType][...]` clause, whatever it currently holds. */
const PERMIT_TYPE_CLAUSE_REG_EXP = /\[permitType\]\[[^\]]+\]/g

export const DIRECT_ASSIGNMENT_KEY = 'direct'

export interface IUserSubmissionData {
  user: User
  submission: IStepSubmission | undefined
}

export interface ISubmissionGroup {
  fieldType: PermitFieldType | typeof DIRECT_ASSIGNMENT_KEY
  fieldCaption: string
  approvedUsers: IUserSubmissionData[]
  pendingUsers: User[]
  /** Set when the group is a one-sign-off-per-rule step's rule. */
  ruleId?: string
  /** Set when the group is a company assignee: one sign-off from anyone in it. */
  companyId?: string
  /** Sign-offs the group needs to be complete: every member when unset, none for sign-offs that count toward nothing. */
  requiredCount?: number
}

/** A rule a one-sign-off-per-rule step waits on, with its sign-off on the step if it has one. */
export interface IBicRuleStatus {
  ruleId: string
  name: string
  memberIds: string[]
  submission: IStepSubmission | undefined
}

export default class PermitBallInCourtStore {
  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly operationRulesStore: OperationRulesStore,
    private readonly projectMembersStore: ProjectMembersStore,
    private readonly workflowTypesStore: WorkflowTypesStore,
    // Names company sign-off groups; without it they read "Company".
    private readonly companiesStore?: CompaniesStore,
  ) {
    makeObservable(this)
  }

  /**
   * The permit-type clause a rule expression must carry. Workflow types are
   * versioned and the id changes on every save; the `type` does not, and it is
   * what the server compares first.
   */
  public makePermitTypeExpression = (workflowTypeId: string): string => {
    const workflowType =
      this.workflowTypesStore.getWorkflowTypeById(workflowTypeId)

    return OperationRule.makeTagExpressionString(
      PERMIT_TYPE_TAG,
      workflowType?.type || workflowTypeId,
    )
  }

  /**
   * The same clause, substituted into an expression that already carries one.
   * A rule duplicated from one written before the rules side moved to `type`
   * would otherwise inherit that rule's version id.
   */
  public withPermitTypeExpression = (
    expression: string,
    workflowTypeId: string,
  ): string =>
    expression.replace(
      PERMIT_TYPE_CLAUSE_REG_EXP,
      this.makePermitTypeExpression(workflowTypeId),
    )

  // The rule edits below change the rule in place and return whether anything changed; saving
  // the rule is the caller's call.

  @action.bound
  public addUserToRule(rule: OperationRule, userId: string): boolean {
    const newUserExpression = OperationRule.makeTagExpressionString(
      TagType.User,
      userId,
    )

    if (rule.assignmentsAsSubexpressions.includes(newUserExpression)) {
      return false
    }

    rule.assignmentsAsSubexpressions.push(newUserExpression)
    rule.updateAssignmentExpressions()
    return true
  }

  @action.bound
  public removeUserFromRule(rule: OperationRule, userId: string): boolean {
    if (!userId || !rule?.assignmentsAsSubexpressions?.length) {
      return false
    }

    const tagExpression = OperationRule.makeTagExpressionString(
      TagType.User,
      userId,
    )
    const { assignmentsAsSubexpressions } = rule
    const expressionIdx = rule.assignmentsAsSubexpressions.findIndex(
      v => v === tagExpression,
    )

    if (expressionIdx === -1) {
      return false
    }

    assignmentsAsSubexpressions.splice(expressionIdx, 1)
    if (!assignmentsAsSubexpressions.length) {
      assignmentsAsSubexpressions.push(OperationRule.ASSIGNMENT_PLACEHOLDER)
    }
    rule.updateAssignmentExpressions()
    return true
  }

  /** Applies one assignee picker save: users, user fields and companies together. */
  @action.bound
  public applyDiffToRule(
    rule: OperationRule,
    addedUserIds: string[],
    removedUserIds: string[],
    addedFieldIds: string[],
    removedFieldIds: string[],
    addedCompanyIds: string[] = [],
    removedCompanyIds: string[] = [],
  ): boolean {
    const hasUserChanges = this.applyAssigneeDiff(
      rule,
      TagType.User,
      addedUserIds,
      removedUserIds,
    )
    const hasFieldChanges = this.applyAssigneeDiff(
      rule,
      'field',
      addedFieldIds,
      removedFieldIds,
    )
    const hasCompanyChanges = this.applyAssigneeDiff(
      rule,
      COMPANY_ASSIGNEE_TAG_TYPE,
      addedCompanyIds,
      removedCompanyIds,
    )

    return this.commitAssignees(
      rule,
      hasUserChanges || hasFieldChanges || hasCompanyChanges,
    )
  }

  /** A company assignee is every project member whose company on the project it is. */
  @action.bound
  public applyCompanyDiffToRule(
    rule: OperationRule,
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ): boolean {
    return this.commitAssignees(
      rule,
      this.applyAssigneeDiff(
        rule,
        COMPANY_ASSIGNEE_TAG_TYPE,
        addedCompanyIds,
        removedCompanyIds,
      ),
    )
  }

  private applyAssigneeDiff(
    rule: OperationRule,
    tagType: TagType | string,
    addedIds: string[],
    removedIds: string[],
  ): boolean {
    let hasChanges = false
    const { assignmentsAsSubexpressions } = rule

    removedIds.forEach(id => {
      const expressionIdx = assignmentsAsSubexpressions.indexOf(
        OperationRule.makeTagExpressionString(tagType, id),
      )

      if (expressionIdx !== -1) {
        assignmentsAsSubexpressions.splice(expressionIdx, 1)
        hasChanges = true
      }
    })

    addedIds.forEach(id => {
      const tagExpression = OperationRule.makeTagExpressionString(tagType, id)

      if (!assignmentsAsSubexpressions.includes(tagExpression)) {
        assignmentsAsSubexpressions.push(tagExpression)
        hasChanges = true
      }
    })

    return hasChanges
  }

  private commitAssignees(rule: OperationRule, hasChanges: boolean): boolean {
    if (hasChanges) {
      const { assignmentsAsSubexpressions } = rule
      if (!assignmentsAsSubexpressions.length) {
        assignmentsAsSubexpressions.push(OperationRule.ASSIGNMENT_PLACEHOLDER)
      }
      rule.updateAssignmentExpressions()
    }

    return hasChanges
  }

  @action.bound
  public addFieldToRule(rule: OperationRule, fieldId: string): boolean {
    const newFieldExpression = OperationRule.makeTagExpressionString(
      'field',
      fieldId,
    )

    if (rule.assignmentsAsSubexpressions.includes(newFieldExpression)) {
      return false
    }

    rule.assignmentsAsSubexpressions.push(newFieldExpression)
    rule.updateAssignmentExpressions()
    return true
  }

  @action.bound
  public removeFieldFromRule(rule: OperationRule, fieldId: string): boolean {
    if (!fieldId || !rule?.assignmentsAsSubexpressions?.length) {
      return false
    }

    const tagExpression = OperationRule.makeTagExpressionString(
      'field',
      fieldId,
    )
    const { assignmentsAsSubexpressions } = rule
    const expressionIdx = rule.assignmentsAsSubexpressions.findIndex(
      v => v === tagExpression,
    )

    if (expressionIdx === -1) {
      return false
    }

    assignmentsAsSubexpressions.splice(expressionIdx, 1)
    if (!assignmentsAsSubexpressions.length) {
      assignmentsAsSubexpressions.push(OperationRule.ASSIGNMENT_PLACEHOLDER)
    }
    rule.updateAssignmentExpressions()
    return true
  }

  @action.bound
  public addFieldsToRule(rule: OperationRule, fieldIds: string[]): boolean {
    let hasChanges = false

    fieldIds.forEach(fieldId => {
      const newFieldExpression = OperationRule.makeTagExpressionString(
        'field',
        fieldId,
      )

      if (!rule.assignmentsAsSubexpressions.includes(newFieldExpression)) {
        rule.assignmentsAsSubexpressions.push(newFieldExpression)
        hasChanges = true
      }
    })

    if (hasChanges) {
      rule.updateAssignmentExpressions()
    }

    return hasChanges
  }

  @action.bound
  public removeFieldsFromRule(
    rule: OperationRule,
    fieldIds: string[],
  ): boolean {
    if (!fieldIds?.length || !rule?.assignmentsAsSubexpressions?.length) {
      return false
    }

    let hasChanges = false
    const { assignmentsAsSubexpressions } = rule

    fieldIds.forEach(fieldId => {
      const tagExpression = OperationRule.makeTagExpressionString(
        'field',
        fieldId,
      )
      const expressionIdx = assignmentsAsSubexpressions.findIndex(
        v => v === tagExpression,
      )

      if (expressionIdx !== -1) {
        assignmentsAsSubexpressions.splice(expressionIdx, 1)
        hasChanges = true
      }
    })

    if (hasChanges) {
      if (!assignmentsAsSubexpressions.length) {
        assignmentsAsSubexpressions.push(OperationRule.ASSIGNMENT_PLACEHOLDER)
      }
      rule.updateAssignmentExpressions()
    }

    return hasChanges
  }

  public isUserAllowedByBicRules = (
    userId: string,
    permit: Workflow,
    ruleIds: string[],
    completionMode: BicCompletionMode = BicCompletionMode.AnyOne,
  ): boolean => {
    if (completionMode === BicCompletionMode.OnePerRule) {
      // In court while one of the user's rules still needs a sign-off
      return this.getPendingRulesForUser(userId, permit, ruleIds).length > 0
    }

    const rules = this.getPermitBicRulesByIds(ruleIds)

    if (completionMode === BicCompletionMode.AllUsers) {
      // In court while a sign-off the user can give is still missing
      const submittedUserIds = new Set(
        permit.getSubmittedUserIds(permit.currentWorkflowStepId),
      )
      return this.getRequiredSignOffs(rules, permit).some(
        memberIds =>
          memberIds.includes(userId) &&
          !memberIds.some(id => submittedUserIds.has(id)),
      )
    }

    // Original "any user" logic
    const rulesByUser = this.getBicRulesByUserIdOrField(rules, userId, permit)

    return rulesByUser.some(rule => this.isRuleValidForPermit(rule, permit))
  }

  private isRuleValidForPermit = (
    rule: OperationRule,
    permit: Workflow,
  ): boolean => {
    const { locationsWithEquipment, companyIds } = permit

    const areLocationTagsValid =
      !locationsWithEquipment?.length ||
      locationsWithEquipment.some(l => rule.isLocationTagValid(l.id))

    const areCompanyTagsValid =
      !companyIds?.length || companyIds.some(id => rule.isCompanyTagValid(id))

    return areLocationTagsValid && areCompanyTagsValid
  }

  public canStepBeCompleted = (
    permit: Workflow,
    stepId: string,
    ruleIds: string[],
    completionMode: BicCompletionMode,
  ): boolean => {
    if (completionMode === BicCompletionMode.AnyOne) {
      return true
    }

    if (!ruleIds?.length) {
      return true
    }

    if (completionMode === BicCompletionMode.OnePerRule) {
      return this.getRuleStatuses(permit, stepId, ruleIds).every(
        status => !!status.submission,
      )
    }

    const rules = this.getPermitBicRulesByIds(ruleIds)
    const submittedUserIds = new Set(permit.getSubmittedUserIds(stepId))

    return this.getRequiredSignOffs(rules, permit).every(memberIds =>
      memberIds.some(id => submittedUserIds.has(id)),
    )
  }

  /**
   * The sign-offs an all-users step waits on, each as the people who can give it: every user
   * the rules name directly or through a user field signs for themselves, and a company is one
   * sign-off anyone from it can give. A user in a listed company signs once for both.
   */
  private getRequiredSignOffs = (
    rules: OperationRule[],
    permit: Workflow,
  ): string[][] => {
    const { userIds, companySignOffs } = this.getRequiredAssignees(
      rules,
      permit,
    )

    return [...userIds.map(id => [id]), ...companySignOffs]
  }

  /** The users an all-users step's rules name, and its companies as the members who can sign for them. */
  private getRequiredAssignees = (
    rules: OperationRule[],
    permit: Workflow,
  ): { userIds: string[]; companySignOffs: string[][] } => {
    const userIds = new Set<string>()
    const companyIds = new Set<string>()

    rules.forEach(rule => {
      if (!this.isRuleValidForPermit(rule, permit)) {
        return
      }

      this.getNamedUserIds(rule, permit).forEach(id => userIds.add(id))
      rule.assignedCompanyIds.forEach(id => companyIds.add(id))
    })

    const companySignOffs = [...companyIds]
      .map(this.getCompanyMemberIds)
      .filter(memberIds => memberIds.length > 0)

    return { userIds: [...userIds], companySignOffs }
  }

  /** What an all-users step's rules assign on the workflow: users, companies or both. */
  public getRequiredAssigneeKinds = (
    permit: Workflow,
    ruleIds: string[],
  ): BicAssignees => {
    const { userIds, companySignOffs } = this.getRequiredAssignees(
      this.getPermitBicRulesByIds(ruleIds),
      permit,
    )

    if (!companySignOffs.length) {
      return 'users'
    }

    return userIds.length ? 'usersAndCompanies' : 'companies'
  }

  public isUserInRequiredBicUsers = (
    userId: string,
    permit: Workflow,
    ruleIds: string[],
  ): boolean => {
    const rules = this.getPermitBicRulesByIds(ruleIds)
    if (!rules.length) return false

    const allRequiredUserIds = this.getAllRequiredUserIds(rules, permit)
    return allRequiredUserIds.includes(userId)
  }

  public getAllRequiredUserIds = (
    rules: OperationRule[],
    permit: Workflow,
  ): string[] => {
    const userIds = new Set<string>()

    rules.forEach(rule => {
      if (!this.isRuleValidForPermit(rule, permit)) {
        return
      }

      this.getRuleMemberIds(rule, permit).forEach(id => userIds.add(id))
    })

    return Array.from(userIds)
  }

  /**
   * The rules a one-sign-off-per-rule step waits on: those whose conditions match the workflow
   * and that resolve to at least one person, in the order the configurator lists them. A rule
   * that resolves to nobody needs no sign-off. Unnamed rules are called by their position.
   */
  public getRuleStatuses = (
    permit: Workflow,
    stepId: string,
    ruleIds: string[],
  ): IBicRuleStatus[] =>
    this.getPermitBicRulesByIds(ruleIds)
      .sort((a, b) => a.createdAt - b.createdAt)
      .map((rule, index) => ({ rule, index }))
      .filter(({ rule }) => this.isRuleValidForPermit(rule, permit))
      .map(({ rule, index }) => ({
        ruleId: rule.id,
        name: rule.name || `${Localization.translator.rule} ${index + 1}`,
        memberIds: this.getRuleMemberIds(rule, permit),
        submission: permit.getRuleSubmission(stepId, rule.id),
      }))
      .filter(status => status.memberIds.length > 0)

  /** The rules on the workflow's current step that still need a sign-off and include the user. */
  public getPendingRulesForUser = (
    userId: string,
    permit: Workflow,
    ruleIds: string[],
  ): IBicRuleStatus[] =>
    this.getRuleStatuses(permit, permit.currentWorkflowStepId, ruleIds).filter(
      status => !status.submission && status.memberIds.includes(userId),
    )

  /**
   * The company a sign-off on a step counts for: the signer's company when the step's rules list
   * it as an assignee, or, on a one-sign-off-per-rule step, when the rule signed for does.
   */
  public getSignOffCompanyName = (
    permit: Workflow,
    submission: IStepSubmission,
    stepRuleIds: string[],
  ): string | undefined => {
    const ruleIds = submission.ruleId ? [submission.ruleId] : stepRuleIds
    const companyId = this.getPermitBicRulesByIds(ruleIds)
      .filter(rule => this.isRuleValidForPermit(rule, permit))
      .flatMap(rule => rule.assignedCompanyIds)
      .find(id => this.getCompanyMemberIds(id).includes(submission.userId))

    if (!companyId) {
      return undefined
    }

    return (
      this.companiesStore?.getCompanyNameById(companyId) ||
      Localization.translator.company
    )
  }

  /** Everyone the rule names: directly, through a user field, or through their company. */
  private getRuleMemberIds = (
    rule: OperationRule,
    permit: Workflow,
  ): string[] => {
    const userIds = new Set(this.getNamedUserIds(rule, permit))

    rule.assignedCompanyIds.forEach(companyId =>
      this.getCompanyMemberIds(companyId).forEach(id => userIds.add(id)),
    )

    return Array.from(userIds)
  }

  /** The users the rule names directly or through a user field, leaving companies out. */
  private getNamedUserIds = (
    rule: OperationRule,
    permit: Workflow,
  ): string[] => {
    const userIds = new Set<string>()

    this.getUsersFromAssignment(rule.assignmentsAsSubexpressions).forEach(
      user => user?.id && userIds.add(user.id),
    )
    rule.assignedFieldIds?.forEach(fieldId =>
      this.getUserIdsFromPermitField(permit, fieldId).forEach(id =>
        userIds.add(id),
      ),
    )

    return Array.from(userIds)
  }

  private getCompanyMemberIds = (companyId: string): string[] =>
    this.projectMembersStore.getCompanyMembers(companyId).map(user => user.id)

  public getApprovalStatusGroupedByField = (
    permit: Workflow,
    stepId: string,
    ruleIds: string[],
    permitType: WorkflowType,
  ): ISubmissionGroup[] => {
    const rules = this.getPermitBicRulesByIds(ruleIds).filter(Boolean)
    const approvedUserIds = new Set(permit.getSubmittedUserIds(stepId))

    const typeFieldMap = new Map<string, IPermitTypeField>()
    permitType.workflowSteps.forEach(step => {
      step.fields?.forEach(field => {
        if (field.id) {
          typeFieldMap.set(field.id, field)
        }
      })
    })

    const permitFieldMap = new Map(
      permit.fields?.map(f => [f.fieldId, f]) || [],
    )

    const getUserIdsFromField = (fieldId: string): string[] => {
      const field = permitFieldMap.get(fieldId)
      if (!field?.values?.length) return []
      return field.values.filter(
        (v): v is string => typeof v === 'string' && v.length > 0,
      )
    }

    const usersBySource = new Map<string, Set<string>>()
    const companyIds = new Set<string>()

    rules.forEach(rule => {
      if (!this.isRuleValidForPermit(rule, permit)) {
        return
      }

      rule.assignedCompanyIds.forEach(id => companyIds.add(id))

      const directUserIds = rule.assignedUserIds || []
      directUserIds.forEach(userId => {
        if (!usersBySource.has(DIRECT_ASSIGNMENT_KEY)) {
          usersBySource.set(DIRECT_ASSIGNMENT_KEY, new Set())
        }
        usersBySource.get(DIRECT_ASSIGNMENT_KEY).add(userId)
      })

      const fieldIds = rule.assignedFieldIds || []
      fieldIds.forEach(fieldId => {
        const fieldUserIds = getUserIdsFromField(fieldId)

        if (fieldUserIds.length > 0) {
          if (!usersBySource.has(fieldId)) {
            usersBySource.set(fieldId, new Set())
          }
          fieldUserIds.forEach(id => usersBySource.get(fieldId).add(id))
        }
      })
    })

    const allBicUserIds = new Set<string>()
    usersBySource.forEach(userIds => {
      userIds.forEach(id => allBicUserIds.add(id))
    })

    const groups: ISubmissionGroup[] = []
    usersBySource.forEach((userIds, source) => {
      if (userIds.size === 0) return

      const allUsers = this.getUsersByIds([...userIds])
      const approved = allUsers.filter(u => approvedUserIds.has(u.id))
      const pending = allUsers.filter(u => !approvedUserIds.has(u.id))

      const approvedWithSubmissions: IUserSubmissionData[] = approved.map(
        user => ({
          user,
          submission: permit.getUserSubmission(stepId, user.id),
        }),
      )

      const typeField = typeFieldMap.get(source)
      groups.push({
        fieldType:
          source === DIRECT_ASSIGNMENT_KEY
            ? DIRECT_ASSIGNMENT_KEY
            : (typeField?.type as PermitFieldType),
        fieldCaption: this.getFieldCaption(source, typeField),
        approvedUsers: approvedWithSubmissions,
        pendingUsers: pending,
      })
    })

    // A company needs one sign-off from anyone in it
    companyIds.forEach(companyId => {
      const members = this.projectMembersStore.getCompanyMembers(companyId)
      if (!members.length) {
        return
      }

      members.forEach(member => allBicUserIds.add(member.id))
      const signers = members.filter(member => approvedUserIds.has(member.id))

      groups.push({
        fieldType: PermitFieldType.Company,
        fieldCaption:
          this.companiesStore?.getCompanyNameById(companyId) ||
          Localization.translator.company,
        approvedUsers: signers.map(user => ({
          user,
          submission: permit.getUserSubmission(stepId, user.id),
        })),
        pendingUsers: signers.length ? [] : members,
        companyId,
        requiredCount: 1,
      })
    })

    const nonBicSubmittedUserIds = [...approvedUserIds].filter(
      id => !allBicUserIds.has(id),
    )
    if (nonBicSubmittedUserIds.length > 0) {
      const nonBicUsers = this.getUsersByIds(nonBicSubmittedUserIds)
      const approvedWithSubmissions: IUserSubmissionData[] = nonBicUsers.map(
        user => ({
          user,
          submission: permit.getUserSubmission(stepId, user.id),
        }),
      )

      groups.push({
        fieldType: DIRECT_ASSIGNMENT_KEY,
        fieldCaption: Localization.translator.otherSubmissions,
        approvedUsers: approvedWithSubmissions,
        pendingUsers: [],
      })
    }

    return groups
  }

  /**
   * One group per rule a one-sign-off-per-rule step waits on: its members until one of them
   * signs, then the signer. Sign-offs that count toward no rule are listed apart.
   */
  public getApprovalStatusGroupedByRule = (
    permit: Workflow,
    stepId: string,
    ruleIds: string[],
  ): ISubmissionGroup[] => {
    const statuses = this.getRuleStatuses(permit, stepId, ruleIds)
    const toSubmissionData = (
      submission: IStepSubmission,
    ): IUserSubmissionData[] =>
      this.getUsersByIds([submission.userId]).map(user => ({
        user,
        submission,
      }))

    const groups: ISubmissionGroup[] = statuses.map(status => ({
      fieldType: DIRECT_ASSIGNMENT_KEY,
      fieldCaption: status.name,
      approvedUsers: status.submission
        ? toSubmissionData(status.submission)
        : [],
      pendingUsers: status.submission
        ? []
        : this.getUsersByIds(status.memberIds),
      ruleId: status.ruleId,
      requiredCount: 1,
    }))

    const countedSubmissions = new Set(statuses.map(s => s.submission))
    const otherSubmissions = permit
      .getLatestRoundSubmissions(stepId)
      .filter(submission => !countedSubmissions.has(submission))

    if (otherSubmissions.length > 0) {
      groups.push({
        fieldType: DIRECT_ASSIGNMENT_KEY,
        fieldCaption: Localization.translator.otherSubmissions,
        approvedUsers: otherSubmissions.flatMap(toSubmissionData),
        pendingUsers: [],
        requiredCount: 0,
      })
    }

    return groups
  }

  private getFieldCaption = (
    fieldId: string,
    typeField: IPermitTypeField | undefined,
  ): string => {
    if (fieldId === DIRECT_ASSIGNMENT_KEY) {
      return Localization.translator.directAssignments
    }

    if (typeField?.caption) {
      return typeField.caption
    }

    const { translator } = Localization
    switch (typeField?.type) {
      case PermitFieldType.Assignee:
        return translator.assignee
      case PermitFieldType.Requester:
        return translator.requester
      case PermitFieldType.Subscriber:
        return translator.subscriber
      case PermitFieldType.User:
        return translator.user
      default:
        return fieldId
    }
  }

  public getUsersFromAssignment = (assignmentExpressions: string[]): User[] => {
    if (!assignmentExpressions?.length) {
      return []
    }

    return assignmentExpressions
      .reduce((users, element) => {
        // Field and company assignees name people through the workflow or the project
        if (!element.startsWith(USER_TAG_PREFIX)) {
          return users
        }

        const userDto = this.retrieveUser(element)
        return [...users, userDto]
      }, [])
      .filter(u => !!u)
  }

  public getUsersByIds = (userIds: string[]): User[] => {
    if (!userIds?.length) {
      return []
    }

    return userIds
      .map(id => this.projectMembersStore.getById(id))
      .filter((u): u is User => !!u)
  }

  public getPermitBicRulesByIds = (ids: string[]): OperationRule[] => {
    if (!ids?.length) {
      return []
    }

    return ids
      .map(ruleId => this.operationRulesStore.getRuleById(ruleId))
      .filter(rule => rule?.operationSubject === OperationSubjectType.PermitBic)
  }

  public getNewOperationRule = (permitTypeId: string): OperationRule => {
    return new OperationRule(
      this.makePermitTypeExpression(permitTypeId),
      OperationType.Notification,
      OperationSubjectType.PermitBic,
      this.eventsStore.appState.activeProject.id,
      OperationRule.ASSIGNMENT_PLACEHOLDER,
      OperationRule.NEW_ID,
      Date.now(),
    )
  }

  private retrieveUser = (expressionString: string): User => {
    const [, tagValue] = expressionString.match(TAG_REG_EXP)

    return this.projectMembersStore.getById(tagValue)
  }

  private getBicRulesByUserId = (
    rules: OperationRule[],
    userId: string,
  ): OperationRule[] => {
    return rules.filter(rule => {
      return this.getUsersFromAssignment(
        rule?.assignmentsAsSubexpressions,
      ).some(u => u.id === userId)
    })
  }

  private getBicRulesByUserIdOrField = (
    rules: OperationRule[],
    userId: string,
    permit: Workflow,
  ): OperationRule[] => {
    return rules.filter(rule => {
      const directUsers = this.getUsersFromAssignment(
        rule?.assignmentsAsSubexpressions,
      )
      if (directUsers.some(u => u.id === userId)) {
        return true
      }

      const isNamedThroughField = rule.assignedFieldIds?.some(fieldId =>
        this.getUserIdsFromPermitField(permit, fieldId).includes(userId),
      )
      if (isNamedThroughField) {
        return true
      }

      return rule.assignedCompanyIds.some(companyId =>
        this.getCompanyMemberIds(companyId).includes(userId),
      )
    })
  }

  private getUserIdsFromPermitField = (
    permit: Workflow,
    fieldId: string,
  ): string[] => {
    const field = permit.fields?.find(f => f.fieldId === fieldId)
    if (!field) {
      return []
    }

    const values = field.values
    if (!values?.length) {
      return []
    }

    return values.filter(
      (v): v is string => typeof v === 'string' && v.length > 0,
    )
  }
}
